<script setup lang="ts">
import { computed, defineProps } from "vue";
import { Task, User } from "@/types/v2";
import { useTask } from "@/composables/tasks/useTask";
import { convertStringHumanReadable, getTaskStatusStyles, listStyles } from "@/utils/v2";
import {
  TaskDependencyContext,
  TaskDependencyTag,
} from "@/utils/v2/tasks/dependencies";

interface TaskListItemProps {
  currentUser: User | null;
  task: Task;
  dependencyContext?: TaskDependencyContext;
}

const props = defineProps<TaskListItemProps>();

const { taskStatusText } = useTask();

const statusStyles = computed(() =>
  getTaskStatusStyles(props.task.status, props.currentUser, props.task.startedBy)
);

const objectiveLabel = computed(() => {
  const objective = props.task.objective;
  if (typeof objective === "string") {
    return convertStringHumanReadable(objective);
  }

  return convertStringHumanReadable(objective.label || objective.id);
});

const taskDisplayTitle = computed(() => {
  const additional = props.task.additionalTitle?.trim();
  if (additional) {
    return additional;
  }

  const title = props.task.title?.trim();
  return title || null;
});

const dependencyContext = computed<TaskDependencyContext>(() => {
  return (
    props.dependencyContext ?? {
      tags: [],
      groupColor: null,
      hasUnresolvedDependsOn: false,
    }
  );
});

const taskCardStyle = computed<Record<string, string>>(() => {
  const style: Record<string, string> = {};
  const groupColor = dependencyContext.value.groupColor;

  if (groupColor) {
    style.borderLeft = `4px solid ${groupColor.border}`;
    style.backgroundColor = groupColor.cardBackground;
    return style;
  }

  if (dependencyContext.value.hasUnresolvedDependsOn) {
    style.borderLeft = "4px solid #dc2626";
    style.backgroundColor = "rgba(254, 242, 242, 0.9)";
  }

  return style;
});

const getDependencyTagStyle = (tag: TaskDependencyTag): Record<string, string> => {
  const groupColor = dependencyContext.value.groupColor;
  if (groupColor) {
    return {
      borderColor: groupColor.border,
      color: groupColor.text,
      backgroundColor: groupColor.badgeBackground,
    };
  }

  if (tag.kind === "depends_on") {
    return {
      borderColor: "#fca5a5",
      color: "#b91c1c",
      backgroundColor: "rgba(254, 226, 226, 0.85)",
    };
  }

  return {
    borderColor: "#93c5fd",
    color: "#1e3a8a",
    backgroundColor: "rgba(219, 234, 254, 0.85)",
  };
};
</script>

<template>
  <div :class="listStyles.task.container" :style="taskCardStyle">
    <div :class="listStyles.task.wrapper">
      <div :class="listStyles.task.contentWrapper">
        <div :class="listStyles.task.iconWrapper">
          <svg :class="listStyles.task.icon" viewBox="0 0 16 16" fill="currentColor">
            <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6 0L5 .25V1h1zm0 1v1H5V1c-1.258.015-2.179-.03-2.932.385-.376.207-.675.56-.84.998-.164.438-.222.96-.222 1.617v8c0 .658.058 1.179.223 1.617.164.439.463.789.84.996.752.416 1.673.372 2.931.387h3.004v-1H5c-1.26-.015-2.087-.06-2.453-.262-.183-.1-.29-.212-.387-.472C2.062 13.006 2 12.592 2 12V7c0-.592.063-1.006.16-1.266.098-.26.204-.371.387-.472.367-.203 1.195-.247 2.459-.262H11c1.26.015 2.087.06 2.453.262.184.1.29.212.387.472C13.938 5.994 14 7 14 7v2h.006v3c0 .592-.062 1.006-.16 1.266s-.204.371-.387.472c-.366.202-1.193.247-2.453.262h-3v1h3c1.258-.015 2.179.029 2.931-.387a1.87 1.87 0 0 0 .84-.996c.165-.438.223-.96.223-1.617V4c0-.658-.06-1.179-.225-1.617a1.88 1.88 0 0 0-.837-.998c-.753-.416-1.674-.37-2.932-.385v1h-1V1H11V0l-1 .25V1z"
                fill="rgba(169, 169, 169, 1)"
                font-family="sans-serif"
                font-weight="400"
                overflow="visible"
                style="
                    line-height: normal;
                    text-indent: 0;
                    text-align: start;
                    text-decoration-line: none;
                    text-decoration-style: solid;
                    text-decoration-color: #000000;
                    text-transform: none;
                    isolation: auto;
                    mix-blend-mode: normal;
                    marker: none;"
                white-space="normal"
                color="#000000"
              />
            </svg>
          </svg>
        </div>
        <div class="flex flex-col">
          <h5 :class="listStyles.task.title">
            {{ objectiveLabel }}
            <span v-if="taskDisplayTitle">
              ({{ taskDisplayTitle }})
            </span>
            <span v-if="task.studyTitle" class="font-medium">
              : {{ task.studyTitle }}
            </span>
            <span
              v-for="tag in dependencyContext.tags"
              :key="tag.id"
              class="ml-2 inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold align-middle"
              :style="getDependencyTagStyle(tag)"
            >
              {{ tag.text }}
            </span>
          </h5>
        </div>
      </div>

      <div :class="listStyles.task.meta.wrapper">
        <div class="flex flex-col items-end gap-1">
          <span
            v-if="task.startedBy !== null"
            :class="[listStyles.task.statusBadge, statusStyles.badge]"
          >
            {{ taskStatusText(task) }}
          </span>
        </div>
        <span v-if="task.scheduledOn" :class="listStyles.task.meta.scheduledDate">
          {{ task.scheduledOn }}
        </span>
        <span>
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clip-rule="evenodd"
            />
          </svg>
        </span>
      </div>
    </div>
  </div>
</template>
